import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Grid,
  Paper,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import Header from '../../components/Header';
import {
  createPruebaLaboratorio,
  deletePruebaLaboratorio,
  getCatalogoPruebasLaboratorio,
  updatePruebaLaboratorio,
} from '../../services/laboratorio.services';

const CATALOG_FORM_INITIAL = {
  codigo: '',
  nombre: '',
  grupo: '',
  cantidad_muestras: '1',
  dimensiones: '',
  identificador_inicio: '',
  identificador_fin: '',
  subtipo: '',
  dias_espera: '',
  orden: '',
};

const LaboratorioPruebas = () => {
  const [catalogoPruebas, setCatalogoPruebas] = useState([]);
  const [loadingCatalogo, setLoadingCatalogo] = useState(true);
  const [includeInactive, setIncludeInactive] = useState(false);
  const [catalogForm, setCatalogForm] = useState(CATALOG_FORM_INITIAL);
  const [editingPruebaId, setEditingPruebaId] = useState(null);
  const [submittingCatalog, setSubmittingCatalog] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, msg: '', severity: 'success' });

  const refreshCatalogo = async (incluirInactivas = includeInactive) => {
    setLoadingCatalogo(true);

    try {
      const response = await getCatalogoPruebasLaboratorio(incluirInactivas);
      setCatalogoPruebas(Array.isArray(response) ? response : []);
    } catch (error) {
      setSnackbar({ open: true, msg: error.message, severity: 'error' });
    } finally {
      setLoadingCatalogo(false);
    }
  };

  useEffect(() => {
    refreshCatalogo(includeInactive);
  }, [includeInactive]);

  const handleCatalogFormChange = (field, value) => {
    setCatalogForm((prev) => ({ ...prev, [field]: value }));
  };

  const resetCatalogForm = () => {
    setCatalogForm(CATALOG_FORM_INITIAL);
    setEditingPruebaId(null);
  };

  const buildCatalogPayload = () => {
    const numericOrNull = (value) => {
      if (value === '' || value == null) {
        return null;
      }

      const numeric = Number(value);
      return Number.isNaN(numeric) ? null : numeric;
    };

    return {
      codigo: catalogForm.codigo.trim(),
      nombre: catalogForm.nombre.trim(),
      grupo: catalogForm.grupo.trim() || null,
      cantidad_muestras: Number(catalogForm.cantidad_muestras) || 0,
      dimensiones: catalogForm.dimensiones.trim() || null,
      identificador_inicio: catalogForm.identificador_inicio.trim() || null,
      identificador_fin: catalogForm.identificador_fin.trim() || null,
      subtipo: catalogForm.subtipo.trim() || null,
      dias_espera: numericOrNull(catalogForm.dias_espera),
      orden: numericOrNull(catalogForm.orden),
    };
  };

  const handleCatalogSubmit = async () => {
    if (!catalogForm.codigo.trim() || !catalogForm.nombre.trim() || !catalogForm.cantidad_muestras) {
      setSnackbar({ open: true, msg: 'Complete codigo, nombre y cantidad de muestras', severity: 'warning' });
      return;
    }

    setSubmittingCatalog(true);

    try {
      if (editingPruebaId) {
        await updatePruebaLaboratorio(editingPruebaId, buildCatalogPayload());
        setSnackbar({ open: true, msg: 'Prueba actualizada', severity: 'success' });
      } else {
        await createPruebaLaboratorio(buildCatalogPayload());
        setSnackbar({ open: true, msg: 'Prueba creada', severity: 'success' });
      }

      resetCatalogForm();
      await refreshCatalogo(includeInactive);
    } catch (error) {
      setSnackbar({ open: true, msg: error.message, severity: 'error' });
    } finally {
      setSubmittingCatalog(false);
    }
  };

  const handleEditCatalog = (prueba) => {
    setEditingPruebaId(prueba.id);
    setCatalogForm({
      codigo: prueba.codigo || '',
      nombre: prueba.nombre || '',
      grupo: prueba.grupo || '',
      cantidad_muestras: String(prueba.cantidad_muestras || ''),
      dimensiones: prueba.dimensiones || '',
      identificador_inicio: prueba.identificador_inicio || '',
      identificador_fin: prueba.identificador_fin || '',
      subtipo: prueba.subtipo || '',
      dias_espera: prueba.dias_espera != null ? String(prueba.dias_espera) : '',
      orden: prueba.orden != null ? String(prueba.orden) : '',
    });
  };

  const handleDeleteCatalog = async (pruebaId) => {
    setSubmittingCatalog(true);

    try {
      await deletePruebaLaboratorio(pruebaId);
      setSnackbar({ open: true, msg: 'Prueba desactivada', severity: 'success' });
      await refreshCatalogo(includeInactive);
    } catch (error) {
      setSnackbar({ open: true, msg: error.message, severity: 'error' });
    } finally {
      setSubmittingCatalog(false);
    }
  };

  return (
    <Box m="20px">
      <Header title="Registro de Pruebas" subtitle="Catalogo de pruebas del laboratorio" />

      <Grid container spacing={2} mt={1}>
        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6">Catalogo de pruebas</Typography>
              <FormControlLabel
                control={<Checkbox checked={includeInactive} onChange={(event) => setIncludeInactive(event.target.checked)} />}
                label="Incluir inactivas"
              />
            </Box>

            <Grid container spacing={2} mb={2}>
              <Grid item xs={12} md={2}><TextField fullWidth label="Codigo" value={catalogForm.codigo} onChange={(event) => handleCatalogFormChange('codigo', event.target.value)} /></Grid>
              <Grid item xs={12} md={4}><TextField fullWidth label="Nombre" value={catalogForm.nombre} onChange={(event) => handleCatalogFormChange('nombre', event.target.value)} /></Grid>
              <Grid item xs={12} md={2}><TextField fullWidth label="Grupo" value={catalogForm.grupo} onChange={(event) => handleCatalogFormChange('grupo', event.target.value)} /></Grid>
              <Grid item xs={12} md={2}><TextField fullWidth type="number" label="Cantidad" value={catalogForm.cantidad_muestras} onChange={(event) => handleCatalogFormChange('cantidad_muestras', event.target.value)} /></Grid>
              <Grid item xs={12} md={2}><TextField fullWidth label="Dimensiones" value={catalogForm.dimensiones} onChange={(event) => handleCatalogFormChange('dimensiones', event.target.value)} /></Grid>
              <Grid item xs={12} md={2}><TextField fullWidth label="Id inicio" value={catalogForm.identificador_inicio} onChange={(event) => handleCatalogFormChange('identificador_inicio', event.target.value)} /></Grid>
              <Grid item xs={12} md={2}><TextField fullWidth label="Id fin" value={catalogForm.identificador_fin} onChange={(event) => handleCatalogFormChange('identificador_fin', event.target.value)} /></Grid>
              <Grid item xs={12} md={2}><TextField fullWidth label="Subtipo" value={catalogForm.subtipo} onChange={(event) => handleCatalogFormChange('subtipo', event.target.value)} /></Grid>
              <Grid item xs={12} md={2}><TextField fullWidth type="number" label="Dias espera" value={catalogForm.dias_espera} onChange={(event) => handleCatalogFormChange('dias_espera', event.target.value)} /></Grid>
              <Grid item xs={12} md={2}><TextField fullWidth type="number" label="Orden" value={catalogForm.orden} onChange={(event) => handleCatalogFormChange('orden', event.target.value)} /></Grid>
              <Grid item xs={12} md={2} display="flex" alignItems="center" gap={1}>
                <Button variant="contained" onClick={handleCatalogSubmit} disabled={submittingCatalog}>{editingPruebaId ? 'Actualizar' : 'Crear'}</Button>
                <Button variant="outlined" onClick={resetCatalogForm} disabled={submittingCatalog}>Limpiar</Button>
              </Grid>
            </Grid>

            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Codigo</TableCell>
                    <TableCell>Nombre</TableCell>
                    <TableCell>Grupo</TableCell>
                    <TableCell>Cantidad</TableCell>
                    <TableCell>Dimensiones</TableCell>
                    <TableCell>Identificadores</TableCell>
                    <TableCell>Estado</TableCell>
                    <TableCell>Acciones</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loadingCatalogo ? (
                    <TableRow><TableCell colSpan={8}>Cargando catalogo...</TableCell></TableRow>
                  ) : !catalogoPruebas.length ? (
                    <TableRow><TableCell colSpan={8}>No hay pruebas cargadas.</TableCell></TableRow>
                  ) : (
                    catalogoPruebas.map((prueba) => (
                      <TableRow key={prueba.id}>
                        <TableCell>{prueba.codigo}</TableCell>
                        <TableCell>{prueba.nombre}</TableCell>
                        <TableCell>{prueba.grupo || '-'}</TableCell>
                        <TableCell>{prueba.cantidad_muestras}</TableCell>
                        <TableCell>{prueba.dimensiones || '-'}</TableCell>
                        <TableCell>{prueba.identificador_inicio || '-'} / {prueba.identificador_fin || '-'}</TableCell>
                        <TableCell>{Number(prueba.status_active) === 1 ? 'Activa' : 'Inactiva'}</TableCell>
                        <TableCell>
                          <Box display="flex" gap={1}>
                            <Button size="small" variant="outlined" onClick={() => handleEditCatalog(prueba)}>Editar</Button>
                            {Number(prueba.status_active) === 1 && <Button size="small" color="error" variant="outlined" onClick={() => handleDeleteCatalog(prueba.id)}>Desactivar</Button>}
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Grid>
      </Grid>

      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}>{snackbar.msg}</Alert>
      </Snackbar>
    </Box>
  );
};

export default LaboratorioPruebas;
